import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import BottomNav from "../components/BottomNav";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Megaphone, Users, DollarSign, Clock, ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

const NICHES = ["Fashion", "Beauty", "Fitness", "Food", "Travel", "Tech", "Gaming", "Lifestyle", "Startups", "Product Reviews", "AI"];
const PLATFORMS = [
  { key: "instagram",      label: "Instagram" },
  { key: "tiktok",         label: "TikTok" },
  { key: "youtube",        label: "YouTube" },
  { key: "facebook",       label: "Facebook" },
  { key: "snapchat",       label: "Snapchat" },
  { key: "twitter",        label: "X" },
  { key: "ad_advertising", label: "Ad advertising" },
];
const PLATFORM_LABELS = Object.fromEntries(PLATFORMS.map((p) => [p.key, p.label]));

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.round(ms / 86400000));
}

export default function CampaignsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [niche, setNiche] = useState("all");
  const [platform, setPlatform] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: "open" };
      if (niche !== "all") params.niche = niche;
      if (platform !== "all") params.platform = platform;
      const { data } = await api.get("/campaigns", { params });
      setItems(data);
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Open Campaigns · Creator Briefs"
        description="Brand-posted public briefs open for creator applications. Pitch on paid collabs, get accepted, and start work with escrow protection."
        path="/campaigns"
        keywords={["creator briefs", "influencer campaigns", "paid collabs", "sponsored content briefs", "brand deals for creators", "UGC campaigns"]}
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Open campaigns</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
              Public briefs. <span className="nexus-gradient-text">Apply to work.</span>
            </h1>
            <p className="text-zinc-400 mt-2">Brand-posted campaigns you can pitch on directly.</p>
          </div>
          {user?.role === "brand" && (
            <Link to="/campaigns/new">
              <Button className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90" data-testid="new-campaign-btn">
                <Plus className="h-4 w-4 mr-2" /> Post a campaign
              </Button>
            </Link>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="campaigns-filters">
          <Select value={niche} onValueChange={(v) => { setNiche(v); setTimeout(load, 0); }}>
            <SelectTrigger className="bg-white/[0.02] border-white/10 text-white w-44" data-testid="campaigns-niche-select">
              <SelectValue placeholder="Niche" />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
              <SelectItem value="all">All niches</SelectItem>
              {NICHES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={platform} onValueChange={(v) => { setPlatform(v); setTimeout(load, 0); }}>
            <SelectTrigger className="bg-white/[0.02] border-white/10 text-white w-44" data-testid="campaigns-platform-select">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0C10] border-white/10 text-white">
              <SelectItem value="all">All platforms</SelectItem>
              {PLATFORMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-zinc-500 ml-auto" data-testid="campaigns-count">
            {loading ? "…" : `${items.length} open`}
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-56 rounded-2xl bg-[#0C0C10] hairline animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="campaigns-empty">
            <Megaphone className="h-8 w-8 mx-auto mb-3 text-nexus-purple" />
            <div className="font-display text-2xl mb-2">No open campaigns yet</div>
            <p className="text-zinc-400">
              {user?.role === "brand" ? "Post the first one — creators are waiting." : "Check back soon or get discovered on your public profile."}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campaigns-grid">
            {items.map((c) => {
              const d = daysUntil(c.closes_at);
              return (
                <Link
                  key={c.id}
                  to={`/campaigns/${c.id}`}
                  className="rounded-2xl bg-[#0C0C10] hairline card-hover beam-hover flex flex-col overflow-hidden"
                  data-testid={`campaign-card-${c.id}`}
                >
                  {(c.cover_video_url || c.cover_url) && (
                    <div className="relative aspect-[16/9] bg-black overflow-hidden">
                      {c.cover_video_url ? (
                        <video
                          src={c.cover_video_url}
                          className="w-full h-full object-cover"
                          autoPlay muted loop playsInline
                          data-testid={`campaign-card-video-${c.id}`}
                        />
                      ) : (
                        <img
                          src={c.cover_url}
                          alt={c.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          data-testid={`campaign-card-image-${c.id}`}
                        />
                      )}
                      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c.brand_name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-nexus-blue flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {d}d left
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-xl mb-2">{c.title}</h3>
                  <p className="text-sm text-zinc-400 line-clamp-3 flex-1">{c.description}</p>
                  {(c.platforms || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(c.platforms || []).slice(0, 4).map((p) => (
                        <span key={p} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${p === "ad_advertising" ? "border-nexus-pink/40 bg-nexus-pink/10 text-nexus-pink" : "border-nexus-blue/25 bg-nexus-blue/5 text-nexus-blue"}`}>
                          {p === "ad_advertising" && "🚀 "}
                          {PLATFORM_LABELS[p] || p}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(c.niches || []).slice(0, 3).map((n) => (
                      <span key={n} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.02]">
                        {n}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-white/5">
                    <div>
                      <div className="font-display font-bold nexus-gradient-text text-2xl">£{c.budget}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Budget</div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{c.applications_count || 0}</span>
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
